import React, { useEffect, useRef, useState } from 'react';
import { Box } from '@mui/material';
import DOMPurify from 'dompurify';

interface TruncateProps {
  text: string;
  lines: number;
  onTruncationChange?: (truncated: boolean) => void;
  className?: string;
  showTitle?: boolean;
  sx?: React.CSSProperties;
}

const Truncate: React.FC<TruncateProps> = (props) => {
  const divRef = useRef<HTMLDivElement>(null);
  const [isTruncated, setIsTruncated] = useState(false);

  useEffect(() => {
    if (!props.onTruncationChange || !divRef.current) {
      return;
    }
    
    const observer = new window.ResizeObserver((entries: ResizeObserverEntry[]) => {
      for (const entry of entries) {
        // in some browsers (e.g. Firefox), entry.contentRect.height is denoted by a float value
        // we need to compare pixels, i.e. integers
        const heightAfterTruncation = Math.ceil(entry.contentRect.height);
        const heightWithoutTruncation = entry.target.scrollHeight;
        const isEnoughSpaceForContent = heightWithoutTruncation <= heightAfterTruncation;
        setIsTruncated(!isEnoughSpaceForContent);
      }
    });

    window.requestAnimationFrame(() => {
      if (!divRef.current) return;
      observer.observe(divRef.current);
    });

    return () => {
      if (divRef.current) {
        observer.unobserve(divRef.current);
      }
      observer.disconnect();
    };
  }, []);

  useEffect(() => {
    if (!props.onTruncationChange) {
      return;
    }

    props.onTruncationChange(isTruncated);
  }, [isTruncated]);

  const sx = Object.assign({}, props.sx, {
    display: '-webkit-box',
    WebkitLineClamp: props.lines || 1,
    WebkitBoxOrient: 'vertical',
    overflow: 'hidden',
    textOverflow: 'ellipsis',
  });

  return (
    <Box
      className={`${props.className || ''}`}
      ref={divRef}
      sx={sx}
      dangerouslySetInnerHTML={{ __html: DOMPurify.sanitize(props.text) }}
      title={props.showTitle ? props.text : undefined}
    />
  );
};

export default Truncate;
